import { DataResult, State as DataState } from '@progress/kendo-data-query';
import { GridCellProps } from '@progress/kendo-react-grid';
import _ from 'lodash';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router';
import {
  formatDate,
  formatPrice,
} from '../../../../../../common/global/helpers';
import { DataLoader } from '../../../../../../common/widgets/DataLoader';
import Grid, { GridCellTd } from '../../../../../../common/widgets/Grid';
import Icons from '../../../../../../common/widgets/Icons';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../config';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import {
  CanCancelSubscriptionTypes,
  SubscriptionItemModel,
} from '../../../../models/Subscriptions';
import { resources } from '../../../../resources/resources';
import { productsService } from '../../../../services/products';
import CancelSubscriptionDialog from '../../../Common/CancelSubscriptionDialog';
import {
  EmptyStateDashboardMySubscriptions,
  EmptyStateSubscriptions,
} from '../../../Common/EmptyStates';

interface SubscriptionsGridProps {
  subscriptions: {
    data?: SubscriptionItemModel[];
    total: number;
    dataState: DataState;
    onDataReceived: (data: DataResult, showErrorPanel?: boolean) => void;
    onDataStateChange: (dataState: DataState) => void;
    onSubscriptionCanceled: (orderProductId: number) => any;
  };
  showErrorPanel?: boolean;
  isForDashboard: boolean;
}

export const SubscriptionGrid = (props: SubscriptionsGridProps) => {
  const {
    subscriptions: {
      data,
      total,
      dataState,
      onDataReceived,
      onDataStateChange,
      onSubscriptionCanceled,
    },
    showErrorPanel,
    isForDashboard,
  } = props;
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);

  if (!!data && !data.length)
    return isForDashboard ? (
      <EmptyStateDashboardMySubscriptions />
    ) : (
      <EmptyStateSubscriptions />
    );

  return (
    <>
      <DataLoader
        getData={productsService.getSubscriptions}
        dataState={dataState}
        onDataReceived={onDataReceived}
      />
      <Loader
        loading={!data}
        loader={isForDashboard ? <Skeleton /> : <LoadingSpinner />}
        showErrorPanel={showErrorPanel}
      >
        <div
          className={`my-account-grid acc-subscriptions-grid ${
            customerInfo.isGrossUpUser ? 'gross-up-subscriptions-grid' : ''
          }`}
        >
          <Grid
            data={data}
            pageable={{
              pageSizes: config.grid.pageSizes,
              pageSizeValue: dataState.take,
              info: false,
              previousNext: true,
            }}
            sortable={{ allowUnsort: true, mode: 'single' }}
            {...dataState}
            onDataStateChange={e => onDataStateChange(e.dataState)}
            total={total}
            columns={[
              {
                title: translate(resources.subscriptions.orderId),
                field: 'orderId',
                sortable: false,
                cell: OrderIdCell,
              },
              {
                title: translate(resources.subscriptions.dateAdded),
                field: 'dateAdded',
                sortable: true,
                cell: DateCell,
              },
              {
                title: translate(resources.subscriptions.productName),
                field: 'name',
                sortable: false,
                cell: ProductNameCell,
              },
              {
                title: translate(resources.subscriptions.period),
                sortable: false,
                cell: PeriodCell,
              },
              ...(!customerInfo.isGrossUpUser
                ? [
                    {
                      title: translate(resources.subscriptions.totalTaxes),
                      field: 'totalTaxes',
                      sortable: false,
                      cell: TotalTaxesCell,
                      headerClassName: 'grid-number',
                    },
                  ]
                : []),
              {
                title: translate(resources.subscriptions.total),
                field: 'total',
                sortable: true,
                cell: TotalCell,
                headerClassName: 'grid-number',
              },
              {
                width: 100,
                headerClassName: 'grid-actions-cell',
                cell: props => (
                  <GridCellTd className="grid-actions-cell">
                    <span className="grid-actions">
                      <CancelCell
                        {...props}
                        onSuccess={onSubscriptionCanceled}
                      />
                      <DetailsCell {...props} />{' '}
                    </span>
                  </GridCellTd>
                ),
              },
            ]}
          />
        </div>
      </Loader>
    </>
  );
};

const OrderIdCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>#{dataItem.orderId}</GridCellTd>
);

const DateCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{formatDate(dataItem.dateAdded)}</GridCellTd>
);

const ProductNameCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.name}</GridCellTd>
);

const PeriodCell = ({ dataItem }: GridCellProps) => {
  const text =
    dataItem.endYear === 2999
      ? `${dataItem.startMonth}/${dataItem.startYear}`
      : `${dataItem.startMonth}/${dataItem.startYear} - ${dataItem.endMonth}/${dataItem.endYear}`;
  return (
    <GridCellTd className={`${!dataItem.isExpired ? 'active' : 'inactive'}`}>
      <span className="subscription-period-badge">{text}</span>
    </GridCellTd>
  );
};

const TotalTaxesCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd className="grid-number">
    {formatPrice(dataItem.totalTaxes)}
  </GridCellTd>
);

const TotalCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd className="grid-number">{formatPrice(dataItem.total)}</GridCellTd>
);

interface CancelCellProps extends GridCellProps {
  onSuccess: (orderProductId: number) => void;
}

const CancelCell = (props: CancelCellProps) => {
  const { t: translate } = useTranslation();
  const [subscriptionToRemove, setSubscription] =
    useState<SubscriptionItemModel | null>(null);

  return (
    <>
      {props.dataItem.canCancelSubscription ===
      CanCancelSubscriptionTypes.Show ? (
        <button
          type="button"
          className="btn-link subscription-action subscription-action-cancel"
          onClick={() => setSubscription(props.dataItem)}
        >
          <span className="btn-icon">
            <Icons.DeleteButton />
          </span>
          <span className="btn-text">
            {translate(resources.recurring.cancel)}
          </span>
        </button>
      ) : (
        CanCancelSubscriptionTypes.Disable && (
          <button
            type="button"
            className="btn-link btn-subscription-cancel subscription-action subscription-action-cancel"
            disabled
          >
            <span className="btn-icon">
              <Icons.DeleteButton />
            </span>
            <span className="btn-text">
              {translate(resources.recurring.cancel)}
            </span>
          </button>
        )
      )}
      {!_.isNil(subscriptionToRemove) && (
        <CancelSubscriptionDialog
          product={subscriptionToRemove}
          onSuccess={() => props.onSuccess(subscriptionToRemove.orderProductId)}
          onClose={() => setSubscription(null)}
        />
      )}
    </>
  );
};

const DetailsCell = (props: GridCellProps) => {
  const navigate = useNavigate();
  const { t: translate } = useTranslation();

  return (
    <button
      type="button"
      className="btn-link grid-action-nav subscription-action subscription-action-details"
      onClick={() => navigate(`/my-account/orders/${props.dataItem.orderId}`)}
    >
      <span className="btn-icon">
        <Icons.ArrowButton />
      </span>
      <span className="btn-text">{translate(resources.orders.seeDetails)}</span>
    </button>
  );
};
